/**
 * Simplified DOM observer for reading progress
 * Only handles DOM intersection observation - no business logic
 */
export class ProgressObserver {
  private observer: IntersectionObserver | null = null;
  private readonly visibleBlocks = new Map<number, number>(); // ratio per blockId
  private currentBlock: number | null = null;
  private observedToBlockId = new WeakMap<Element, number>();

  constructor(private readonly onBlockChange: (blockId: number) => void) {}

  startObserving(): void {
    this.observer?.disconnect();

    const containerEl = document.querySelector('[data-highlighting-container]');

    const rootOption =
      containerEl instanceof HTMLElement &&
      containerEl.scrollHeight > containerEl.clientHeight
        ? containerEl
        : null;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const blockId =
            this.observedToBlockId.get(entry.target) ??
            this.getBlockIdFromElement(entry.target);
          if (blockId !== null) {
            this.updateBlockVisibility(blockId, entry);
          }
        });

        this.updateCurrentBlock();
      },
      {
        root: rootOption,
        rootMargin: '0px',
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      },
    );

    const observed = this.observeAllBlocks(false);
    if (observed === 0) {
      setTimeout(() => {
        const retryObserved = this.observeAllBlocks(false);
        if (retryObserved === 0) {
          // Fallback only when no headings exist
          this.observeAllBlocks(true);
        }
      }, 100);
    }
  }

  stopObserving(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.visibleBlocks.clear();
    this.currentBlock = null;
    this.observedToBlockId = new WeakMap();
  }

  refreshContent(): void {
    // Fully restart to recalculate root and anchors reliably after DOM changes
    this.stopObserving();
    this.startObserving();
  }

  // Private methods
  private observeAllBlocks(fallbackToAll: boolean): number {
    if (!this.observer) {
      return 0;
    }

    // Track only headings when available; optionally fallback to all ids
    const anchors = Array.from(
      document.querySelectorAll(
        fallbackToAll
          ? '[id^="b"]'
          : 'h1[id^="b"],h2[id^="b"],h3[id^="b"],h4[id^="b"],h5[id^="b"],h6[id^="b"]',
      ),
    );

    let observedCount = 0;
    anchors.forEach((anchor) => {
      const blockId = this.getBlockIdFromElement(anchor);
      if (blockId === null) {
        return;
      }
      let target: HTMLElement | null =
        anchor instanceof HTMLElement ? anchor : null;
      const MAX_ASCENT_DEPTH = 5;
      let depth = 0;
      while (
        target &&
        target.offsetHeight === 0 &&
        target.parentElement &&
        depth < MAX_ASCENT_DEPTH
      ) {
        target = target.parentElement as HTMLElement | null;
        depth++;
      }
      if (target) {
        this.observedToBlockId.set(target, blockId);
        this.observer?.observe(target);
        observedCount++;
      }
    });

    return observedCount;
  }

  private getBlockIdFromElement(element: Element): number | null {
    const idAttr = element.getAttribute('id');
    if (idAttr && idAttr.startsWith('b')) {
      const num = parseInt(idAttr.slice(1), 10);
      return Number.isNaN(num) ? null : num;
    }
    return null;
  }

  private updateBlockVisibility(
    blockId: number,
    entry: IntersectionObserverEntry,
  ): void {
    const ratio = entry.intersectionRatio;

    if (ratio > 0) {
      this.visibleBlocks.set(blockId, ratio);
    } else {
      this.visibleBlocks.delete(blockId);
    }
  }

  private updateCurrentBlock(): void {
    if (this.visibleBlocks.size === 0) {
      if (this.currentBlock !== null) {
        this.currentBlock = null;
      }
      return;
    }

    let maxRatio = 0;
    let bestBlock = 0;

    this.visibleBlocks.forEach((ratio, blockId) => {
      if (ratio > maxRatio) {
        maxRatio = ratio;
        bestBlock = blockId;
      }
    });

    if (this.currentBlock !== bestBlock) {
      this.currentBlock = bestBlock;
      this.onBlockChange(bestBlock);
    }
  }
}
